import formatCpf from "@brazilian-utils/format-cpf";

export type DocumentKind = "CPF" | "CNPJ" | "CNPJ_ALPHANUMERIC" | "UNKNOWN";

const WEIGHTS_DV1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const WEIGHTS_DV2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

export const sanitizeDocument = (value: string): string =>
  (value ?? "")
    .toString()
    .toUpperCase()
    .normalize("NFD")
    .replace(/\p{Mn}/gu, "")
    .replace(/[^A-Z0-9]/g, "");

export const normalizeDocumentForPayload = (value: string): string =>
  sanitizeDocument(value);

const charToCnpjValue = (c: string): number => {
  if (!c) return -1;
  const code = c.charCodeAt(0);
  if (code >= 48 && code <= 57) return code - 48;
  if (code >= 65 && code <= 90) return code - 48;
  return -1;
};

const calcCnpjCheckDigit = (base: string, weights: number[]): number => {
  let sum = 0;
  for (let i = 0; i < weights.length; i += 1) {
    const v = charToCnpjValue(base[i]);
    if (v < 0) return -1;
    sum += v * weights[i];
  }
  const mod = sum % 11;
  return mod < 2 ? 0 : 11 - mod;
};

export const validateDocumentCNPJ = (value: string): boolean => {
  const raw = sanitizeDocument(value);
  if (raw.length !== 14) return false;

  const d12 = raw.charAt(12);
  const d13 = raw.charAt(13);
  if (!/^\d$/.test(d12) || !/^\d$/.test(d13)) return false;

  const invalidNumericLegacy = [
    "00000000000000",
    "11111111111111",
    "22222222222222",
    "33333333333333",
    "44444444444444",
    "55555555555555",
    "66666666666666",
    "77777777777777",
    "88888888888888",
    "99999999999999",
  ];
  if (/^\d{14}$/.test(raw) && invalidNumericLegacy.includes(raw)) return false;

  if (/^(.)\1{13}$/.test(raw)) return false;

  const dv1 = calcCnpjCheckDigit(raw.slice(0, 12), WEIGHTS_DV1);
  if (dv1 < 0 || dv1 !== Number(d12)) return false;

  const dv2 = calcCnpjCheckDigit(raw.slice(0, 13), WEIGHTS_DV2);
  if (dv2 < 0 || dv2 !== Number(d13)) return false;

  return true;
};

export const getDocumentType = (value: string): DocumentKind => {
  const s = sanitizeDocument(value);
  if (s.length === 11 && /^\d{11}$/.test(s)) return "CPF";
  if (s.length === 14 && /^\d{14}$/.test(s)) return "CNPJ";
  if (s.length === 14) return "CNPJ_ALPHANUMERIC";
  return "UNKNOWN";
};

const formatCnpjMask = (alnum: string): string => {
  const s = alnum.slice(0, 14);
  const max = s.length;
  let out = "";
  for (let i = 0; i < max; i += 1) {
    out += s[i];
    if (i === 1 && max > 2) out += ".";
    else if (i === 4 && max > 5) out += ".";
    else if (i === 7 && max > 8) out += "/";
    else if (i === 11 && max > 12) out += "-";
  }
  return out;
};

export const formatDocumentForDisplay = (value: string): string => {
  const s = sanitizeDocument(value);
  if (s.length === 0) return "";

  if (s.length <= 11) {
    const digitsOnly = s.replace(/[^0-9]/g, "").slice(0, 11);
    if (digitsOnly.length === 0) return "";
    return formatCpf(digitsOnly);
  }

  return formatCnpjMask(s);
};

export const isDocumentLengthInvalid = (value: string): boolean => {
  const s = sanitizeDocument(value);
  if (s.length === 0) return false;
  if (s.length > 14) return true;
  if (s.length >= 1 && s.length <= 10) return true;
  if (s.length === 11 && /[^0-9]/.test(s)) return true;
  if (s.length >= 12 && s.length <= 13) return true;
  return false;
};
